import React, { useState } from 'react';

// ** i18n
import { useTranslation } from 'react-i18next';

// ** MUI
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';

// ** Hooks
import { useTheme } from '@mui/material/styles';
import { useConfirm } from '@erikdakoda/confirm-ui/useConfirm';
import { useCloudinaryUploadWidget } from '../hooks/useCloudinaryUploadWidget';
import { useException } from '@erikdakoda/ui/hooks/useException';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';

// ** Types
import { type CustomControlProps } from '@erikdakoda/editor-ui/types';

// ** Form
import { Controller, type FieldValues } from 'react-hook-form';

// ** Utils
import clsx from 'clsx';
import DialogBox from '@erikdakoda/ui/components/DialogBox';
import PowerIconButton from '@erikdakoda/ui/components/PowerIconButton';
import CloudImage from '@erikdakoda/ui/components/CloudImage';
import type { CloudinaryUploadWidgetOptions } from '../types';
import mergeSxProps from '@erikdakoda/ui/shared/mergeSxProps';

export type ImageUploadControlProps = CustomControlProps<FieldValues> & {
  uploadPreset?: string;
  cropping?: boolean;
  multiple?: boolean;
  uploadButtonTitle?: string;
  /** API route that deletes the Cloudinary asset; the request body is `{ publicId, itemId }`. */
  deleteEndpoint?: string;
};

export default function ImageUploadControl(props: ImageUploadControlProps) {
  'use memo';

  const { t } = useTranslation('cloudinary-ui');
  const {
    item,
    fieldName,
    control,
    fieldSpec,
    errors,
    formControlSx,
    disabled,
    className,
    uploadPreset = 'avatars',
    cropping = false,
    multiple = true,
    uploadButtonTitle = t('uploadScreenshots'),
    deleteEndpoint,
  } = props;

  const theme = useTheme();
  const confirm = useConfirm();
  const exception = useException();
  const { snack } = useSnack();
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const uploadWidgetConfig: Partial<CloudinaryUploadWidgetOptions> = {
    cloudName: 'dakoda',
    uploadPreset,
    cropping,
    multiple,
  };

  const { isUploadWidgetLoading, openUploadWidget } = useCloudinaryUploadWidget({
    uploadWidgetConfig,
  });

  const handleDeleteImage = async (
    publicId: string,
    currentValue: string[],
    onChange: (value: string[]) => void,
  ) => {
    const { confirmed } = await confirm({
      title: t('deleteScreenshotConfirmTitle'),
      description: t('deleteScreenshotConfirmDescription'),
    });
    if (!confirmed) return;

    setDeletingIds((prev) => new Set(prev).add(publicId));

    try {
      const itemId = typeof item.id === 'string' ? item.id : undefined;
      if (deleteEndpoint && itemId) {
        const response = await fetch(deleteEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ publicId, itemId }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || t('deleteScreenshotFailed'));
        }
      }

      const newValue = currentValue.filter((id) => id !== publicId);
      onChange(newValue);
      snack.success(t('screenshotDeleted'));
    } catch (err: unknown) {
      exception(err, t('deleteScreenshotException'));
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(publicId);
        return next;
      });
    }
  };

  const handleImageClick = (publicId: string) => {
    setSelectedImageId(publicId);
    setImageDialogOpen(true);
  };

  if (!item) return null;

  return (
    <>
      <Controller
        // @ts-expect-error react-hook-form Controller does not type className; root className convention
        className="image-upload-control"
        name={fieldName}
        control={control}
        render={({ field: { value, onChange, onBlur } }) => {
          const imageIds: string[] = Array.isArray(value) ? value : [];

          const handleMultipleUploads = () => {
            if (isUploadWidgetLoading) return;

            const currentImageIds = Array.isArray(value) ? value : [];
            openUploadWidget((publicIds) => {
              onChange(publicIds);
              onBlur();
            }, currentImageIds);
          };

          return (
            <FormControl
              className={clsx('image-upload-control', className)}
              disabled={disabled}
              sx={mergeSxProps(formControlSx, { width: '100%' })}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                {imageIds.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                      },
                      gap: 2,
                    }}
                  >
                    {imageIds.map((publicId) => (
                      <Box
                        key={publicId}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          paddingTop: '100%', // Square aspect ratio
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: `1px solid ${theme.palette.divider}`,
                          cursor: 'pointer',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            '&:hover': {
                              opacity: 0.8,
                            },
                          }}
                          onClick={() => handleImageClick(publicId)}
                        >
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              '& .cloud-image': {
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              },
                            }}
                          >
                            <CloudImage
                              cloudId={publicId}
                              steps={[128, 256, 512]}
                              usePlaceholder={true}
                            />
                          </Box>
                        </Box>
                        {!disabled && (
                          <PowerIconButton
                            icon="xmark"
                            title={t('deleteScreenshot')}
                            size="small"
                            danger={true}
                            reverse={true}
                            loading={deletingIds.has(publicId)}
                            disabled={disabled || deletingIds.has(publicId)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteImage(publicId, imageIds, onChange);
                            }}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              padding: 1,
                              backgroundColor: 'background.paper',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              {!disabled && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <PowerIconButton
                    icon="cloud-arrow-up"
                    title={uploadButtonTitle}
                    loading={isUploadWidgetLoading}
                    onClick={handleMultipleUploads}
                  />
                </Box>
              )}

              <FormHelperText error={!!errors[fieldName]} sx={{ textAlign: 'center' }}>
                {errors[fieldName] ? String(errors[fieldName].message) : fieldSpec.helperText}
              </FormHelperText>
            </FormControl>
          );
        }}
      />

      <DialogBox
        isOpen={imageDialogOpen}
        onConfirm={() => setImageDialogOpen(false)}
        onCancel={() => setImageDialogOpen(false)}
        showClose
        hideConfirm
        hideCancel
        maxWidth="xl"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              height: '100%',
            },
          },
        }}
        sx={{
          '& .MuiDialogContent-root': {
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
      >
        {selectedImageId && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              padding: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloudImage cloudId={selectedImageId} steps={[512, 1024, 2048]} />
          </Box>
        )}
      </DialogBox>
    </>
  );
}
